/**
 * Welcome-Set Cart Logic
 *
 * Manages the automatic addition/removal of welcome-set gift items when
 * a Fresh (Frischfutter) product is added to the cart.
 *
 * Responsibilities:
 * - Combined add: gifts and the Fresh item as two sequential /cart/add.js calls,
 *   ordered so Frischfutter is the FIRST cart line
 * - Fresh exclusivity: subscription vs one-time can't coexist
 * - Old gift cleanup on purchase-type switch
 * - Page-load reconcile: swap allergy/standard gifts if configurator changed
 *
 * Configuration is injected via window._welcomeSetConfig (set by Liquid).
 */

/* ───────────────────────────────────────────────────────────────────────────
   Shared fresh-food classifier for Ajax API (/cart.js) line items.
   An IDENTICAL copy lives at the top of assets/cart-and-shop-modern.js, and the
   Liquid equivalent is snippets/cart-is-fresh.liquid. Keep all three in sync.

   Duplicated (rather than imported) because load order between the two assets is
   not guaranteed: this file is script-tagged inside
   snippets/fresh-product-form-modern.liquid, which sits in page content and so
   executes BEFORE the layout-footer cart-and-shop-modern.js on the fresh product
   page. The `if (!window.HB.isFreshLineItem)` guard means whichever file loads
   first installs the one runtime definition.

   It deliberately does not rely on product_type or handle as primary signals:
   both are TRANSLATABLE product fields, so they come back localised on /fr.
   /cart.js exposes no metafields or tags and the live fresh SKUs contain no
   "fresh" token, so `properties._category` is the only translation-proof signal
   available here — see the fuller note in assets/cart-and-shop-modern.js.
   Installed outside the IIFE below, which early-returns when there is no
   welcome-set config.
   ─────────────────────────────────────────────────────────────────────────── */
window.HB = window.HB || {};
if (!window.HB.isFreshLineItem) {
  window.HB.isFreshLineItem = function (item) {
    if (!item) return false;

    var props = item.properties || {};

    // 1. Line-item property, written at add-to-cart time. Never translated.
    //    The only translation-proof signal available to JS.
    var propCategory = String(props._category || props.category || '').trim().toLowerCase();
    if (propCategory === 'fresh') return true;

    // 2. LEGACY translatable fallbacks — correct on the primary locale only.
    if (String(item.product_type || '').trim().toLowerCase() === 'fresh') return true;
    if (String(item.handle || '').toLowerCase().indexOf('frischfutter') !== -1) return true;

    return false;
  };
}

(function () {
  'use strict';

  var config = window._welcomeSetConfig;
  if (!config) return;

  var ALLERGY_COMPONENTS = config.allergyComponents;
  var ALLERGY_COMPONENTS_ONETIME = config.allergyComponentsOnetime;
  var SIZE_COMPONENTS = config.sizeComponents || {};
  var SIZE_COMPONENTS_ONETIME = config.sizeComponentsOnetime || {};
  var ALLERGY_TRIGGERS = config.allergyTriggers;
  var HEALTH_TRIGGERS = config.healthTriggers;

  // Ordered smallest -> largest. Used to validate a configurator value, to pick
  // the largest size in multi-pet carts, and to cascade to the smallest filled
  // size when the dog's size is unknown.
  var SIZE_ORDER = ['dwarf', 'small', 'medium', 'large', 'giant'];

  var isAddingWelcomeSet = false;

  // ── Pure helpers ───────────────────────────────────────────────────────────

  /**
   * Standard-branch list for a purchase type. The standard set IS the per-size
   * set - there is no size-agnostic fallback field. Resolution order:
   *   1. the dog's exact size, if that field is filled;
   *   2. otherwise the smallest FILLED size (dwarf -> giant), so a dog with an
   *      unknown size still receives gifts as long as any size is configured.
   * Returns [] only when every size field is empty.
   */
  function pickStandardList(sizeMap, size) {
    if (size && sizeMap[size] && sizeMap[size].length) return sizeMap[size];
    for (var i = 0; i < SIZE_ORDER.length; i++) {
      var list = sizeMap[SIZE_ORDER[i]];
      if (list && list.length) return list;
    }
    return [];
  }

  function resolveLists(purchaseType, size) {
    if (purchaseType === 'one_time') {
      return {
        standard: pickStandardList(SIZE_COMPONENTS_ONETIME, size),
        allergy: ALLERGY_COMPONENTS_ONETIME
      };
    }
    return {
      standard: pickStandardList(SIZE_COMPONENTS, size),
      allergy: ALLERGY_COMPONENTS
    };
  }

  function resolveBranch(tokens) {
    if (!tokens || !tokens.length) return 'standard';
    var all = ALLERGY_TRIGGERS.concat(HEALTH_TRIGGERS).map(function (t) { return t.toLowerCase(); });
    return tokens.some(function (t) { return all.indexOf(t) !== -1; }) ? 'allergy' : 'standard';
  }

  function buildGiftList(branch, cartTypes, size) {
    // Breed size only differentiates the standard branch. The allergy set is
    // size-agnostic by design.
    var effectiveSize = branch === 'allergy' ? null : size;
    var subList = resolveLists('subscription', effectiveSize)[branch] || [];
    var otList = resolveLists('one_time', effectiveSize)[branch] || [];

    function typeFor(id) {
      var inSub = subList.some(function (c) { return c.id === id; });
      var inOt = otList.some(function (c) { return c.id === id; });
      if (inSub && inOt) return 'both';
      return inSub ? 'subscription' : 'one_time';
    }

    var active = [];
    if (cartTypes.subscription) active.push(subList);
    if (cartTypes.one_time) active.push(otList);

    var byId = {}, order = [];
    active.forEach(function (list) {
      (list || []).forEach(function (c) {
        if (!c || !c.id || byId[c.id]) return;
        byId[c.id] = { id: c.id, name: c.name, forType: typeFor(c.id) };
        order.push(c.id);
      });
    });
    return order.map(function (id) { return byId[id]; });
  }

  function readConfiguratorTriggers() {
    try {
      var raw = localStorage.getItem('configurator.pets') || localStorage.getItem('configurator');
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      var pets = Array.isArray(parsed) ? parsed
        : (parsed && Array.isArray(parsed.pets)) ? parsed.pets
        : (parsed && typeof parsed === 'object') ? [parsed] : [];
      var tokens = [];
      pets.forEach(function (pet) {
        if (!pet) return;
        ['allergies', 'healthIssues'].forEach(function (key) {
          var val = pet[key];
          if (Array.isArray(val)) {
            val.forEach(function (v) { if (v) tokens.push(String(v).trim().toLowerCase()); });
          } else if (typeof val === 'string') {
            val.toLowerCase().split(',').forEach(function (v) { var s = v.trim(); if (s) tokens.push(s); });
          }
        });
      });
      return tokens;
    } catch (e) { return []; }
  }

  function readConfiguratorPets() {
    try {
      var raw = localStorage.getItem('configurator.pets') || localStorage.getItem('configurator');
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed
        : (parsed && Array.isArray(parsed.pets)) ? parsed.pets
        : (parsed && typeof parsed === 'object') ? [parsed] : [];
    } catch (e) { return []; }
  }

  /**
   * Breed size for the cart, read from the PetCon configurator's `breedSize`
   * field (dwarf | small | medium | large | giant).
   *
   * Multi-pet carts get ONE welcome set, so a single size has to be chosen. We
   * take the LARGEST size present, matching how the allergy branch already
   * behaves (any pet with an allergy switches the whole cart to the allergy
   * set) - the customer is never under-gifted.
   *
   * Returns null when no pet has a recognised size, which makes the caller fall
   * back to the size-agnostic standard list.
   */
  function readConfiguratorBreedSize() {
    var best = -1;
    readConfiguratorPets().forEach(function (pet) {
      if (!pet || !pet.breedSize) return;
      var idx = SIZE_ORDER.indexOf(String(pet.breedSize).trim().toLowerCase());
      if (idx > best) best = idx;
    });
    return best === -1 ? null : SIZE_ORDER[best];
  }

  // ── Multi-pet payload (decode _i) ───────────────────────────────────────────
  // DUMMY multi-pet: the PetCon product URL encodes one line item PER dog in the
  // base64 `_i` param (persisted as `product_payload`). The product form only
  // edits the FIRST dog; the remaining dogs are added straight from this payload
  // so a single product page can drop N subscription lines into the cart at once.
  function decodeBase64Utf8(b64) {
    try {
      var binary = atob(b64);
      var bytes = new Uint8Array(binary.length);
      for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new TextDecoder('utf-8').decode(bytes);
    } catch (e) {
      return null;
    }
  }

  function readPayloadItems() {
    try {
      var raw = localStorage.getItem('product_payload');
      if (!raw) return [];
      var json = decodeBase64Utf8(raw);
      if (!json) return [];
      var data = JSON.parse(json);
      return Array.isArray(data.items) ? data.items : [];
    } catch (e) {
      return [];
    }
  }

  // Thin wrapper over the shared classifier installed at the top of this file,
  // so this module and cart-and-shop-modern.js can never drift apart on what
  // counts as a fresh item.
  function isFreshItem(item) {
    return window.HB.isFreshLineItem(item);
  }

  function buildAddPayload(missing, branch, size) {
    var key = branch === 'allergy' ? '_allergy_welcome_set' : '_subscription_welcome_set';
    return {
      items: missing.map(function (c) {
        var props = {};
        props[key] = 'true';
        props['_welcome_set_for'] = c.forType || 'both';
        // Traceability for ops/debugging: which breed-size set produced this
        // line. Underscore-prefixed, so it stays hidden from the customer-facing
        // cart while remaining visible on the order in the Shopify admin.
        if (branch !== 'allergy' && size) props['_welcome_set_size'] = size;
        return { id: c.id, quantity: 1, properties: props };
      })
    };
  }

  // ── Cart busy state ────────────────────────────────────────────────────────

  function setCartBusy(busy) {
    document.querySelectorAll('#CartDrawerForm, #CartPageForm').forEach(function (form) {
      form.classList.toggle('cart--busy', !!busy);
    });
  }

  // ── Fresh exclusivity ──────────────────────────────────────────────────────

  function enforceFreshExclusivity(addedVariantId, purchaseType) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var toRemove = (cart.items || []).filter(function (item) {
          if (!isFreshItem(item)) return false;
          var isSub = !!item.selling_plan_allocation;
          if (purchaseType === 'subscription') return !isSub;
          // one_time: remove all subscriptions + any one-time that isn't the
          // variant we're about to add
          if (isSub) return true;
          return item.variant_id !== addedVariantId;
        });
        if (!toRemove.length) return cart;
        var updates = {};
        toRemove.forEach(function (item) { updates[item.key] = 0; });
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates })
        }).then(function (r) { return r.json(); });
      })
      .catch(function (err) { console.warn('[welcome-set] exclusivity failed:', err); });
  }

  // ── Remove old gifts ───────────────────────────────────────────────────────

  function removeOldGifts(cart) {
    var items = (cart && cart.items) || [];
    var updates = {};
    items.forEach(function (item) {
      var p = item.properties || {};
      if (p['_subscription_welcome_set'] || p['_allergy_welcome_set']) {
        updates[item.key] = 0;
      }
    });
    if (!Object.keys(updates).length) return Promise.resolve();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  // ── Add helpers ────────────────────────────────────────────────────────────

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); });
  }

  function isAddError(data) {
    return !!(data && (data.status === 422 || data.status >= 400));
  }

  function addErrorText(data) {
    return (data && (data.description || data.message)) || 'unknown error';
  }

  function clearGifts() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(removeOldGifts)
      .catch(function (err) { console.warn('[welcome-set] gift rollback failed:', err); });
  }

  /**
   * Which end of the cart a newly added line lands on: 'newest_first' (this
   * store) or 'oldest_first'. Written by window.HB.ensureFreshFirst() after it
   * verifies a reorder, and read here to pick the add order below.
   *
   * Read from localStorage rather than through window.HB, because this file is
   * script-tagged in page content and runs BEFORE the layout-footer
   * cart-and-shop-modern.js. The key name is the whole contract.
   */
  function readCartPlacement() {
    try { return localStorage.getItem('hb_cart_line_placement') || 'newest_first'; }
    catch (e) { return 'newest_first'; }
  }

  // ── Combined add (form submit override) ────────────────────────────────────

  function initCombinedAdd() {
    var formEl = document.querySelector('#add-to-cart-form');
    if (!formEl) return;

    formEl.addEventListener('submit', function (evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();

      var addBtn = formEl.querySelector('[type="submit"], .add-to-cart');
      if (addBtn && addBtn.classList.contains('btn--loading')) return;
      if (addBtn) addBtn.classList.add('btn--loading');

      var pTypeEl = formEl.querySelector('input[name="purchase_type"]:checked');
      var pType = pTypeEl ? pTypeEl.value : 'subscription';

      // Build Fresh item from form
      var variantId = parseInt(formEl.querySelector('[name="id"]').value, 10);
      var qty = 1;
      var qtyInput = formEl.querySelector('[name="quantity"]');
      if (qtyInput) qty = parseInt(qtyInput.value, 10) || 1;

      var spInput = formEl.querySelector('input[name="selling_plan"]:checked') || formEl.querySelector('[name="selling_plan"]');
      var sellingPlan = (pType === 'subscription' && spInput && spInput.value) ? parseInt(spInput.value, 10) : undefined;

      var properties = {};
      formEl.querySelectorAll('input[name^="properties["]').forEach(function (input) {
        var m = input.name.match(/properties\[(.+?)\]/);
        if (m) properties[m[1]] = input.value;
      });

      // Guarantee the translation-proof fresh marker on this line even if the
      // hidden input is missing from the form. `_category` is the primary signal
      // for both snippets/cart-is-fresh.liquid and window.HB.isFreshLineItem(),
      // and it is what keeps the /fr cart identical to the German one — the
      // product_type and handle fallbacks are localised on /fr.
      properties._category = 'fresh';

      var freshItem = { id: variantId, quantity: qty, properties: properties };
      if (sellingPlan) freshItem.selling_plan = sellingPlan;

      // DUMMY multi-pet: when the PetCon payload carries more than one dog, ALL
      // dogs come from that payload (kept in sync by multi-pet-plans.js on the
      // product page; the single-product form controls are hidden). For a single
      // dog we use the form-built item as before.
      var payloadItems = readPayloadItems();
      var freshItems;
      if (payloadItems.length > 1) {
        freshItems = payloadItems
          .map(function (it) {
            if (!it || !it.id) return null;
            var props = it.properties || {};
            // Guarantee the translation-proof fresh marker on every line, the
            // same way the single-dog path does above. petcon-api now emits it,
            // but a line built straight from the payload must not depend on that
            // property surviving — without it isFreshLineItem() falls back to
            // the localised product_type / handle checks, which only hold on /de.
            props._category = 'fresh';
            var fi = { id: parseInt(it.id, 10), quantity: 1, properties: props };
            if (pType === 'subscription' && it.selling_plan) {
              fi.selling_plan = parseInt(it.selling_plan, 10);
            }
            return fi;
          })
          .filter(Boolean);
      } else {
        freshItems = [freshItem];
      }

      // Compute gift items
      var branch = resolveBranch(readConfiguratorTriggers());
      var size = readConfiguratorBreedSize();
      var cartTypes = pType === 'one_time' ? { one_time: true } : { subscription: true };
      var gifts = buildGiftList(branch, cartTypes, size);
      var giftItems = buildAddPayload(gifts, branch, size).items;

      // Both return the FRESH add's response (what `ajaxProduct:added` wants), or
      // null when the fresh add failed and the caller should stop.
      function reportFreshFailure(data) {
        console.error('[welcome-set] Add error:', addErrorText(data));
        setCartBusy(false);
        if (addBtn) addBtn.classList.remove('btn--loading');
        return null;
      }

      // Gifts are a bonus on top of the purchase. A gift failure must never cost
      // the customer the Fresh line they actually asked for, so it is logged and
      // swallowed; reconcileOnPageLoad() tops the gifts back up next page load.
      function addGiftsSwallowing() {
        if (!giftItems.length) return Promise.resolve();
        return addItems(giftItems)
          .then(function (giftData) {
            if (isAddError(giftData)) {
              console.error('[welcome-set] Gift add error:', addErrorText(giftData));
            }
          })
          .catch(function (err) {
            console.error('[welcome-set] Gift add failed:', err);
          });
      }

      // Shopify appends new lines at the END.
      function addFreshThenGifts() {
        return addItems(freshItems).then(function (freshData) {
          if (isAddError(freshData)) return reportFreshFailure(freshData);
          return addGiftsSwallowing().then(function () { return freshData; });
        });
      }

      // Shopify PREPENDS new lines (this store), so fresh goes in last to land
      // at index 0.
      function addGiftsThenFresh() {
        return addGiftsSwallowing()
          .then(function () { return addItems(freshItems); })
          .then(function (freshData) {
            if (!isAddError(freshData)) return freshData;
            // Gifts are now orphaned; clear them rather than leave a gift-only
            // cart, which validateGifts would flag and checkout would refuse.
            return clearGifts().then(function () { return reportFreshFailure(freshData); });
          });
      }

      setCartBusy(true);

      // 1. Remove conflicting Fresh items
      enforceFreshExclusivity(variantId, pType)
        // 2. Remove old welcome-set gifts
        .then(function (cart) { return removeOldGifts(cart); })
        // 3. Add Fresh and the gifts in TWO SEPARATE calls — one for all Fresh
        //    lines, one for all gifts — ordered so a Fresh line ends up FIRST in
        //    the cart (the order the checkout summary shows).
        //
        //    The fresh-vs-gift SPLIT must be two calls: Shopify does not preserve
        //    payload order and on this store it PREPENDS, so adding gifts first
        //    and Fresh last is what keeps the free welcome set out of the head of
        //    checkout. Within the Fresh call, multiple dogs ARE sent as one
        //    multi-item add, so their order relative to each other is not
        //    controlled — a cart showing dog 2 above dog 1 is expected. Only
        //    "a Fresh line is at index 0" is guaranteed (see ensureFreshFirst),
        //    not which dog. The drawer sorts fresh to the top itself, so verify
        //    against /cart.js, not the drawer.
        .then(function () {
          return readCartPlacement() === 'oldest_first'
            ? addFreshThenGifts()
            : addGiftsThenFresh();
        })
        .then(function (data) {
          // null = the fresh add failed and already reported itself.
          if (!data) return;

          var product = Array.isArray(data.items) ? data.items[0] : data;
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: product, addToCartBtn: addBtn, _combinedAdd: true }
          }));
          theme.cart.applyDiscountFromCookie();
          if (addBtn) addBtn.classList.remove('btn--loading');
        })
        .catch(function (err) {
          console.error('[welcome-set] Combined add failed:', err);
          setCartBusy(false);
          if (addBtn) addBtn.classList.remove('btn--loading');
        });
    }, true);
  }

  // ── Page-load reconcile ────────────────────────────────────────────────────

  function reconcileOnPageLoad() {
    if (isAddingWelcomeSet) return;
    isAddingWelcomeSet = true;

    var branch = resolveBranch(readConfiguratorTriggers());
    var size = readConfiguratorBreedSize();

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var cartTypes = { subscription: false, one_time: false };
        items.forEach(function (item) {
          if (!isFreshItem(item)) return;
          if (item.selling_plan_allocation) cartTypes.subscription = true;
          else cartTypes.one_time = true;
        });
        if (!cartTypes.subscription && !cartTypes.one_time) cartTypes.subscription = true;

        var list = buildGiftList(branch, cartTypes, size);
        if (!list.length) return;

        var existing = items.filter(function (item) {
          var p = item.properties || {};
          return p['_subscription_welcome_set'] || p['_allergy_welcome_set'];
        });
        if (!existing.length) return; // No gifts yet = nothing to reconcile

        // Find stale (wrong-branch) items
        var allowed = {};
        list.forEach(function (c) { allowed[c.id] = true; });
        var stale = existing.filter(function (item) { return !allowed[item.variant_id]; });

        var cleanup = stale.length === 0 ? Promise.resolve(cart) : (function () {
          var updates = {};
          stale.forEach(function (item) { updates[item.key] = 0; });
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ updates: updates })
          }).then(function (r) { return r.json(); });
        })();

        return cleanup.then(function (freshCart) {
          var present = {};
          ((freshCart && freshCart.items) || []).forEach(function (i) { if (i) present[i.variant_id] = true; });
          var missing = list.filter(function (c) { return !present[c.id]; });
          if (!missing.length) return;
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(buildAddPayload(missing, branch, size))
          });
        });
      })
      .catch(function (err) { console.warn('[welcome-set] Reconcile failed:', err); })
      .finally(function () { isAddingWelcomeSet = false; });
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initCombinedAdd();
      reconcileOnPageLoad();
    });
  } else {
    initCombinedAdd();
    reconcileOnPageLoad();
  }
})();
